// ============================================================================
// CustomExerciseForm — create a custom exercise in a Sheet. Optional-first: the
// English name is the only required field; every other input is dropped from
// the payload when blank. Categorical fields use SelectField populated from the
// catalog's own facet vocabulary (so a custom row files alongside the rest);
// muscles are entered as a comma-separated list and stored as the backend's
// comma-joined text. Submits via useCreateCustomExercise, which invalidates the
// catalog so the new row appears live.
// ============================================================================
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Banner,
  Button,
  SelectField,
  TextField,
} from "../../components";
import type { SelectOption } from "../../components";
import { useCreateCustomExercise } from "../../hooks/exercises";
import type { CustomExerciseInput, Exercise } from "../../api";
import { Sheet } from "./Sheet";
import { titleCase, type FacetKey, type Modality } from "./lib";

interface CustomExerciseFormProps {
  open: boolean;
  onClose: () => void;
  /** Whole catalog — used to offer the existing facet values as options. */
  catalog: Exercise[];
  /** Called with the new exercise so the page can open + favourite it. */
  onCreated?: (exercise: Exercise) => void;
}

const PATTERN_VALUES = [
  "squat", "hinge", "lunge", "push", "pull", "carry", "rotation", "gait",
];
const LEVEL_VALUES = ["beginner", "intermediate", "expert"];
const MECHANIC_VALUES = ["compound", "isolation"];
// Modality maps to a representative backend category the API understands.
const MODALITY_TO_CATEGORY: Record<Modality, string> = {
  resistance: "strength",
  cardio: "cardio",
  circuit: "plyometrics",
  mobility: "stretching",
};

interface FormState {
  name_en: string;
  name_zh: string;
  modality: Modality;
  movement_pattern: string;
  equipment: string;
  level: string;
  mechanic: string;
  primary_muscles: string;
  secondary_muscles: string;
  description_en: string;
  instructions_en: string;
}

function emptyForm(): FormState {
  return {
    name_en: "",
    name_zh: "",
    modality: "resistance",
    movement_pattern: "",
    equipment: "",
    level: "",
    mechanic: "",
    primary_muscles: "",
    secondary_muscles: "",
    description_en: "",
    instructions_en: "",
  };
}

export function CustomExerciseForm({
  open,
  onClose,
  catalog,
  onCreated,
}: CustomExerciseFormProps) {
  const { t } = useTranslation("exercises");
  const create = useCreateCustomExercise();

  const [form, setForm] = useState<FormState>(emptyForm());
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(emptyForm());
    setTouched(false);
    create.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // Equipment options come from the catalog so a custom row files alongside it.
  const equipmentOptions = useMemo(
    () => distinctOptions(catalog, "equipment"),
    [catalog],
  );

  const nameError = touched && !form.name_en.trim() ? t("custom.nameRequired") : undefined;

  const submit = () => {
    setTouched(true);
    if (!form.name_en.trim()) return;

    const body: CustomExerciseInput = { name_en: form.name_en.trim() };
    const put = (key: keyof CustomExerciseInput, value: string) => {
      const v = value.trim();
      if (v) (body as Record<string, unknown>)[key] = v;
    };
    put("name_zh", form.name_zh);
    body.category = MODALITY_TO_CATEGORY[form.modality];
    put("movement_pattern", form.movement_pattern);
    put("equipment", form.equipment);
    put("level", form.level);
    put("mechanic", form.mechanic);
    put("primary_muscles", normalizeMuscles(form.primary_muscles));
    put("secondary_muscles", normalizeMuscles(form.secondary_muscles));
    put("description_en", form.description_en);
    put("instructions_en", form.instructions_en);

    create.mutate(body, {
      onSuccess: (created) => {
        onCreated?.(created);
        onClose();
      },
    });
  };

  const opt = (values: string[], key: FacetKey | "mechanic"): SelectOption[] =>
    values.map((v) => ({
      value: v,
      label:
        key === "mechanic"
          ? titleCase(v)
          : t(`values.${key}.${v}`, titleCase(v)),
    }));

  const modalityOptions: SelectOption[] = (
    ["resistance", "cardio", "circuit", "mobility"] as Modality[]
  ).map((m) => ({ value: m, label: t(`values.modality.${m}`, titleCase(m)) }));

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={t("custom.title")}
      subtitle={t("custom.subtitle")}
      footer={
        <div className="flex items-center justify-end gap-3">
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
            {t("custom.cancel")}
          </Button>
          <Button onClick={submit} disabled={create.isPending}>
            {create.isPending ? t("custom.saving") : t("custom.save")}
          </Button>
        </div>
      }
    >
      <form
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField
            label={t("custom.nameEn")}
            value={form.name_en}
            onChange={(e) => set("name_en", e.target.value)}
            placeholder={t("custom.namePlaceholder")}
            required
            autoComplete="off"
            error={nameError}
          />
          <TextField
            label={t("custom.nameZh")}
            value={form.name_zh}
            onChange={(e) => set("name_zh", e.target.value)}
            autoComplete="off"
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <SelectField
            label={t("custom.modality")}
            value={form.modality}
            onChange={(e) => set("modality", e.target.value as Modality)}
            options={modalityOptions}
          />
          <SelectField
            label={t("custom.movementPattern")}
            value={form.movement_pattern}
            onChange={(e) => set("movement_pattern", e.target.value)}
            placeholder={t("custom.optional")}
            options={opt(PATTERN_VALUES, "movement_pattern")}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <SelectField
            label={t("custom.equipment")}
            value={form.equipment}
            onChange={(e) => set("equipment", e.target.value)}
            placeholder={t("custom.optional")}
            options={equipmentOptions}
          />
          <SelectField
            label={t("custom.level")}
            value={form.level}
            onChange={(e) => set("level", e.target.value)}
            placeholder={t("custom.optional")}
            options={opt(LEVEL_VALUES, "level")}
          />
          <SelectField
            label={t("custom.mechanic")}
            value={form.mechanic}
            onChange={(e) => set("mechanic", e.target.value)}
            placeholder={t("custom.optional")}
            options={opt(MECHANIC_VALUES, "mechanic")}
          />
        </div>

        <TextField
          label={t("custom.primaryMuscles")}
          value={form.primary_muscles}
          onChange={(e) => set("primary_muscles", e.target.value)}
          placeholder={t("custom.musclesPlaceholder")}
          hint={t("custom.musclesHint")}
          autoComplete="off"
        />
        <TextField
          label={t("custom.secondaryMuscles")}
          value={form.secondary_muscles}
          onChange={(e) => set("secondary_muscles", e.target.value)}
          placeholder={t("custom.musclesPlaceholder")}
          autoComplete="off"
        />

        <Field label={t("custom.description")} htmlFor="cx-desc">
          <textarea
            id="cx-desc"
            value={form.description_en}
            onChange={(e) => set("description_en", e.target.value)}
            rows={2}
            className={TEXTAREA_CLASS}
          />
        </Field>

        <Field label={t("custom.instructions")} htmlFor="cx-instr">
          <textarea
            id="cx-instr"
            value={form.instructions_en}
            onChange={(e) => set("instructions_en", e.target.value)}
            rows={4}
            placeholder={t("custom.instructionsPlaceholder")}
            className={TEXTAREA_CLASS}
          />
        </Field>

        {create.isError && (
          <Banner tone="danger" title={t("custom.saveError")}>
            {t("custom.saveErrorBlurb")}
          </Banner>
        )}
      </form>
    </Sheet>
  );
}

const TEXTAREA_CLASS =
  "w-full resize-y rounded-md bg-surface px-4 py-3 text-base text-body placeholder:text-body-faint ring-1 ring-inset ring-line-strong shadow-xs transition-shadow duration-fast focus:ring-2 focus:ring-inset focus:ring-volt focus-visible:outline-none";

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div className="w-full">
      <label
        htmlFor={htmlFor}
        className="mb-1.5 block font-display text-sm font-bold text-body"
      >
        {label}
      </label>
      {children}
    </div>
  );
}

/** Distinct catalog values for a column → SelectOptions, canonical-ish order. */
function distinctOptions(
  catalog: Exercise[],
  key: "equipment",
): SelectOption[] {
  const seen = new Set<string>();
  for (const ex of catalog) {
    const v = (ex[key] ?? "").trim().toLowerCase();
    if (v) seen.add(v);
  }
  return [...seen]
    .sort()
    .map((v) => ({ value: v, label: titleCase(v) }));
}

/** Lower-case + trim a comma list so custom muscles match the facet vocabulary. */
function normalizeMuscles(raw: string): string {
  return raw
    .split(",")
    .map((m) => m.trim().toLowerCase())
    .filter(Boolean)
    .join(", ");
}
